import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  AUTO_LOGOUT_OPTIONS,
  clearSessionStarted,
  formatRemaining,
  formatSessionElapsed,
  markSessionStarted,
  readAutoLogoutMinutes,
  readSessionStartedAt,
  writeAutoLogoutMinutes,
} from '../lib/sessionTimer';

/**
 * Relógio de sessão + auto-logout absoluto desde o login.
 */
export function useOfficeSessionTimer({ onAutoLogout } = {}) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [startedAt, setStartedAt] = useState(() => readSessionStartedAt());
  const [limitMinutes, setLimitMinutes] = useState(() => readAutoLogoutMinutes());
  const [now, setNow] = useState(() => Date.now());
  const warnedRef = useRef(false);
  const loggingOutRef = useRef(false);
  const userId = user && user !== false ? user.id || user._id || user.email : null;

  useEffect(() => {
    if (!userId) {
      clearSessionStarted();
      setStartedAt(null);
      warnedRef.current = false;
      loggingOutRef.current = false;
      return undefined;
    }
    const ts = markSessionStarted({ force: false });
    setStartedAt(ts);
    return undefined;
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [userId]);

  const elapsedMs = startedAt ? Math.max(0, now - startedAt) : 0;
  const limitMs = limitMinutes > 0 ? limitMinutes * 60 * 1000 : 0;
  const remainingMs = limitMs > 0 ? Math.max(0, limitMs - elapsedMs) : null;
  const expired = limitMs > 0 && elapsedMs >= limitMs;
  const nearEnd = remainingMs != null && remainingMs > 0 && remainingMs <= 5 * 60 * 1000;

  useEffect(() => {
    if (!userId || !expired || loggingOutRef.current) return;
    loggingOutRef.current = true;
    toast.message(t('session.expiredToast'));
    Promise.resolve(onAutoLogout?.()).finally(() => {
      clearSessionStarted();
    });
  }, [expired, userId, onAutoLogout, t]);

  useEffect(() => {
    if (!nearEnd || warnedRef.current || expired) return;
    warnedRef.current = true;
    toast.message(t('session.warnToast', { time: formatRemaining(remainingMs) }));
  }, [nearEnd, expired, remainingMs, t]);

  useEffect(() => {
    if (limitMinutes === 0 || remainingMs == null || remainingMs > 5 * 60 * 1000) {
      warnedRef.current = false;
    }
  }, [limitMinutes, remainingMs]);

  const setAutoLogoutMinutes = useCallback(
    (minutes) => {
      const next = writeAutoLogoutMinutes(minutes);
      setLimitMinutes(next);
      warnedRef.current = false;
      loggingOutRef.current = false;
      const opt = AUTO_LOGOUT_OPTIONS.find((o) => o.minutes === next);
      toast.success(
        next === 0 ? t('session.savedOff') : t('session.savedOn', { label: t(opt?.labelKey || 'session.opt.8h') }),
      );
    },
    [t],
  );

  const resetClock = useCallback(() => {
    const ts = markSessionStarted({ force: true });
    setStartedAt(ts);
    warnedRef.current = false;
    loggingOutRef.current = false;
  }, []);

  const elapsedLabel = useMemo(() => formatSessionElapsed(elapsedMs), [elapsedMs]);

  return {
    active: Boolean(userId),
    startedAt,
    elapsedMs,
    elapsedLabel,
    limitMinutes,
    remainingMs,
    remainingLabel: remainingMs != null ? formatRemaining(remainingMs) : null,
    nearEnd,
    expired,
    setAutoLogoutMinutes,
    resetClock,
  };
}
